// src/components/pmt3/dashboards/FarmingBasicsDashboard.tsx
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { usePmt3FarmingBasicsData, FarmingBasicsDisplayData, GroupAsinInfo } from '@/hooks/usePmt3FarmingBasicsData';
import { Skeleton } from '@/components/ui/skeleton'; // For loading state
import { AlertCircle } from 'lucide-react'; // For error state
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'; // For error state

// Helper function to format currency
const formatCurrency = (value: any): string => {
  if (typeof value === 'number') {
    return value.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
  }
  const num = parseFloat(value);
  if (!isNaN(num)) {
     return num.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
  }
  return value ?? '-';
};

// Helper component for rendering dashboard items
const DashboardItem: React.FC<{
    label: string;
    value: any; // Formatted value for display
    oldValue?: any; // Formatted old value for display
    override?: any; // Formatted override value for display
    overrideDays?: any;
    rawValue?: any; // Raw value for comparison
    rawOldValue?: any; // Raw old value for comparison
}> = ({ label, value, oldValue, override, overrideDays, rawValue, rawOldValue }) => {
    // Determine if old value should be displayed or replaced with '-'
    const compareValue = rawValue !== undefined ? rawValue : value;
    const compareOldValue = rawOldValue !== undefined ? rawOldValue : oldValue;
    // Show old value only if it exists and is different from the current value
    const showOldValue = compareOldValue !== undefined && compareOldValue !== null && String(compareOldValue) !== String(compareValue);

    return (
        <TableRow>
            <TableCell className="font-medium py-2 px-4">{label}</TableCell>
            <TableCell className="py-2 px-4">{value ?? '-'}</TableCell>
            {/* CORRECTED: Apply comparison logic universally */}
            <TableCell className="py-2 px-4">{showOldValue ? (oldValue ?? '-') : '-'}</TableCell>
            {/* Separate Override column - Show only for specific label */}
            <TableCell className="py-2 px-4">
                {label === 'Final Farming Cost Per New CST' && override !== undefined && override !== '-' ? override : ''}
            </TableCell>
            {/* Separate Override Days Ago column - Show only for specific label */}
            <TableCell className="py-2 px-4">
                 {label === 'Final Farming Cost Per New CST' && overrideDays !== undefined && typeof overrideDays === 'number' ? `(${overrideDays} days ago)` : ''}
            </TableCell>
        </TableRow>
    );
};


// Helper component for rendering the Broad Seeds URL link
const BroadSeedsLink: React.FC<{ url: string | null }> = ({ url }) => {
    if (!url) {
        return <span>-</span>;
    }
    try {
        if (!url.startsWith('http://') && !url.startsWith('https://')) {
             throw new Error("Invalid protocol");
        }
        new URL(url);
        return <a href={url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Link</a>;
    } catch (_) {
        console.warn("Invalid Broad Seeds URL:", url);
        return <span>{url}</span>;
    }
};


const FarmingBasicsDashboard: React.FC = () => {
  const { farmingBasicsData, groupAsins, groupCount, loading, error } = usePmt3FarmingBasicsData();

  // --- Loading State ---
  if (loading) {
    return (
      <div className="space-y-6 p-4">
         <Card>
            <CardHeader><Skeleton className="h-6 w-1/3 mb-2" /></CardHeader>
            <CardContent><div className="space-y-3">{[...Array(11)].map((_, i) => (<div key={i} className="flex justify-between"><Skeleton className="h-4 w-1/5" /><Skeleton className="h-4 w-1/5" /><Skeleton className="h-4 w-1/5" /><Skeleton className="h-4 w-1/5" /><Skeleton className="h-4 w-1/5" /></div>))}</div></CardContent> {/* Adjusted skeleton widths for 5 columns */}
        </Card>
         <Card>
            <CardHeader><Skeleton className="h-6 w-1/4 mb-2" /></CardHeader>
            <CardContent><div className="space-y-3">{[...Array(3)].map((_, i) => (<div key={i} className="flex justify-between"><Skeleton className="h-4 w-2/5" /><Skeleton className="h-4 w-2/5" /></div>))}</div></CardContent>
        </Card>
      </div>
    );
  }

  // --- Error State ---
  if (error) {
    return (
        <div className="p-4">
            <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>Error Loading Dashboard</AlertTitle>
                <AlertDescription>
                    Failed to load Farming Basics data: {error.message}. Please try selecting the ASIN again or contact support if the issue persists.
                </AlertDescription>
            </Alert>
        </div>
    );
  }

  // --- No Data State ---
  if (!farmingBasicsData) {
     return (
        <div className="p-4 text-center text-gray-500">
            No Farming Basics data available for the selected ASIN.
        </div>
    );
  }

  // --- Success State ---
  const {
    daysSinceFarming, timesFarmed, timesFarmedOld, timeFarmingActive,
    lifetimeDaysFarming, lifetimeDaysFarmingOld, lifetimeTermsFarming, lifetimeTermsFarmingOld,
    finalCostPerCst, finalCostPerCstOld, finalCostPerCstOverride, finalCostPerCstOverrideDaysAgo,
    farmingGroup, farmingGroupOld, otherAsinsInGroup, shareTermsInGroup, shareTermsInGroupOld,
    broadSeedsUrl, broadSeedsType, broadSeedsTypeOld
  } = farmingBasicsData;

  return (
    <div className="space-y-6 p-4">
      {/* Farming Basics Table */}
      <Card>
        <CardHeader>
          <CardTitle>Farming Basics</CardTitle>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="py-2 px-4">Item</TableHead>
                <TableHead className="py-2 px-4">Value</TableHead>
                <TableHead className="py-2 px-4">Old Value</TableHead>
                <TableHead className="py-2 px-4">Override</TableHead>
                <TableHead className="py-2 px-4">Override Days Ago</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {/* Pass raw values for comparison where needed */}
              <DashboardItem label="Days Since Farming" value={daysSinceFarming} />
              <DashboardItem label="Times Farmed" value={timesFarmed} oldValue={timesFarmedOld} rawValue={timesFarmed} rawOldValue={timesFarmedOld} />
              <DashboardItem label="Time Farming, if Actively Farming" value={timeFarmingActive} />
              <DashboardItem label="Lifetime Days Farming" value={lifetimeDaysFarming} oldValue={lifetimeDaysFarmingOld} rawValue={lifetimeDaysFarming} rawOldValue={lifetimeDaysFarmingOld} />
              <DashboardItem label="Lifetime Terms from Farming" value={lifetimeTermsFarming} oldValue={lifetimeTermsFarmingOld} rawValue={lifetimeTermsFarming} rawOldValue={lifetimeTermsFarmingOld} />
              <DashboardItem
                label="Final Farming Cost Per New CST"
                value={formatCurrency(finalCostPerCst)}
                oldValue={formatCurrency(finalCostPerCstOld)}
                override={formatCurrency(finalCostPerCstOverride)}
                overrideDays={finalCostPerCstOverrideDaysAgo}
                rawValue={finalCostPerCst}
                rawOldValue={finalCostPerCstOld}
              />
              <DashboardItem label="Farming Group" value={farmingGroup} oldValue={farmingGroupOld} rawValue={farmingGroup} rawOldValue={farmingGroupOld} />
              <DashboardItem label="Other ASINs in Farming Group" value={otherAsinsInGroup} />
              <DashboardItem label="Share Terms in Farming Group" value={shareTermsInGroup} oldValue={shareTermsInGroupOld} rawValue={shareTermsInGroup} rawOldValue={shareTermsInGroupOld} />
              <DashboardItem label="Broad Seeds URL" value={<BroadSeedsLink url={broadSeedsUrl} />} />
              <DashboardItem label="Broad Seeds Type" value={broadSeedsType} oldValue={broadSeedsTypeOld} rawValue={broadSeedsType} rawOldValue={broadSeedsTypeOld} />
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* In Farming Group Table */}
      <Card>
         <CardHeader>
           <CardTitle>In Farming Group ({groupCount})</CardTitle>
         </CardHeader>
         <CardContent className="overflow-x-auto">
           <Table>
             <TableHeader>
               <TableRow>
                 <TableHead className="py-2 px-4">Product Name</TableHead>
                 <TableHead className="py-2 px-4">ASIN</TableHead>
               </TableRow>
             </TableHeader>
             <TableBody>
               {groupAsins.length > 0 ? (
                 groupAsins.map((item) => (
                   <TableRow key={item.asin}>
                     <TableCell className="py-2 px-4">{item.productName}</TableCell>
                     <TableCell className="py-2 px-4">{item.asin}</TableCell>
                   </TableRow>
                 ))
               ) : (
                 <TableRow>
                   <TableCell colSpan={2} className="text-center py-4 px-4">No other ASINs found in this group.</TableCell>
                 </TableRow>
               )}
             </TableBody>
           </Table>
         </CardContent>
       </Card>
    </div>
  );
};

export default FarmingBasicsDashboard;